import {useAccessToken} from "../../services/auth"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {findOrgs, Org} from "../../resources/org"
import Loading from "../../components/Loading/Loading"
import React, {useEffect, useState} from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import Card from "../../containers/Card/Card"
import Back from "../../components/Back/Back"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {createBillpayTerms, BillpayTerms, PaymentMethodTerms} from "../../resources/billpayTerms"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"
import {ErrorDocument} from "../../resources/common"
import {useNavigate} from "react-router-dom"
import {useToasts} from "react-toast-notifications"
import {v4 as uuidv4} from "uuid"
import TieredFeeEditor from "./TieredFeeEditor/TieredFeeEditor"
import {validatePaymentMethodFees} from "./billPayTermsValidation"
import {HorizontalField} from "./HorizontalField"

function Footer({state}: {state: AsyncResultIdleRequestState<BillpayTerms, ErrorDocument>}) {
	return (
		<div className="card-content">
			{state.match(
				() => (
					<button className="button is-success">Create</button>
				),
				() => (
					<button className="button is-success is-loading">Creating</button>
				),
				(_) => null,
				(err) => (
					<>
						<div className="has-text-danger">{`${err.errors[0].title} - ${err.errors[0].detail || ""}`}</div>
						<button className="button is-success">Create</button>
					</>
				)
			)}
		</div>
	)
}

type Props = {
	orgs: Array<Org>
}

function getDefaultPaymentMethodTerms(): PaymentMethodTerms {
	return {
		allowed: false,
		fee: null,
	}
}

function Page({orgs}: Props) {
	const accessToken = useAccessToken()
	const navigate = useNavigate()
	const {addToast} = useToasts()

	const [state, create] = useAsyncResultIdle(createBillpayTerms)
	const [name, setName] = useState("")
	const [orgId, setOrgId] = useState(orgs[0]?.id.toString() ?? "")
	const [ach, setAch] = useState<PaymentMethodTerms>(getDefaultPaymentMethodTerms())
	const [check, setCheck] = useState<PaymentMethodTerms>(getDefaultPaymentMethodTerms())
	const [wire, setWire] = useState<PaymentMethodTerms>(getDefaultPaymentMethodTerms())
	const [sameDayAch, setSameDayAch] = useState<PaymentMethodTerms>(getDefaultPaymentMethodTerms())
	const [externalFunding, setExternalFunding] = useState(false)

	useEffect(() => {
		if (state.isOk()) {
			addToast("Bill Pay Terms Created Successfully", {appearance: "success"})
			navigate(`/billpay-terms/${state.value.id}`, {replace: true})
		}
	}, [state])

	const submit = () => {
		// Validate that fees are provided when payment methods are allowed
		if (
			!validatePaymentMethodFees(
				[
					{label: "ACH", terms: ach},
					{label: "Check", terms: check},
					{label: "Wire", terms: wire},
					{label: "Same Day ACH", terms: sameDayAch},
				],
				addToast
			)
		) {
			return
		}

		create(accessToken, name, orgId, externalFunding, ach, check, wire, sameDayAch, uuidv4())
	}

	const disabled = state.match(
		() => false,
		() => true,
		(_) => true,
		(_) => false
	)

	return (
		<>
			<TitleBar title="New Bill Pay Terms" />
			<MainSection>
				<div className="columns">
					<div className="column">
						<div className="buttons">
							<Back />
						</div>
					</div>
				</div>
				<form
					onSubmit={(e) => {
						e.preventDefault()
						submit()
					}}
				>
					<fieldset disabled={disabled}>
						<div className="columns">
							<div className="column">
								<Card title="General">
									<div className="card-content">
										<HorizontalField label="Name">
											<input
												className="input"
												type="text"
												placeholder="checking"
												value={name}
												onChange={(e) => setName(e.target.value)}
												required
											/>
										</HorizontalField>
										<HorizontalField label="Org">
											<div className="select">
												<select value={orgId} onChange={(e) => setOrgId(e.target.value)} required>
													{orgs.map((org) => (
														<option key={org.id} value={org.id}>
															{org.attributes.name}
														</option>
													))}
												</select>
											</div>
										</HorizontalField>
										<HorizontalField label="Status">
											<input className="input" type="text" value="Active" disabled readOnly />
										</HorizontalField>
										<HorizontalField label="External Funding">
											<label className="checkbox">
												<input
													type="checkbox"
													checked={externalFunding}
													onChange={(e) => setExternalFunding(e.target.checked)}
												/>{" "}
												Enable
											</label>
										</HorizontalField>
									</div>
								</Card>
							</div>
						</div>

						<div className="columns">
							<div className="column">
								<div className="card billpay-terms-form">
									<div className="card-header">
										<p className="card-header-title">Transaction Fees</p>
									</div>
									<div className="card-content transaction-fees-content">
										<TieredFeeEditor label="ACH" value={ach} onChange={setAch} isEditing={true} />
										<TieredFeeEditor
											label="Same Day ACH"
											value={sameDayAch}
											onChange={setSameDayAch}
											isEditing={true}
										/>
										<TieredFeeEditor label="Wire" value={wire} onChange={setWire} isEditing={true} />
										<TieredFeeEditor label="Check" value={check} onChange={setCheck} isEditing={true} />
									</div>
								</div>
							</div>
						</div>

						<div className="columns">
							<div className="column">
								<div className="card">
									<Footer state={state} />
								</div>
							</div>
						</div>
					</fieldset>
				</form>
			</MainSection>
		</>
	)
}

export default function NewBillPayTerms() {
	const accessToken = useAccessToken()
	const orgs = useAsyncResult(() => findOrgs(accessToken, 0, 10000))

	return orgs.match(
		() => <Loading />,
		(orgs) => <Page orgs={orgs} />,
		(_) => null
	)
}
